import React from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ArrowDownIcon } from '@components/icons/svg-icon.tsx';
import { getDeviceKey } from '@modules/digital-signage/utils/digital-signage.utils.ts';
import { DevicesChipsAutocompleteProps } from '@modules/digital-signage/model/devices.model.ts';

const CHIP_TAGS_SX = {
  '& .MuiAutocomplete-clearIndicator': { visibility: 'visible' },
  '& .MuiChip-deleteIcon': { margin: '0 -2px 0 2px !important' },
  '& .MuiChip-label': { padding: '0 4px' },
  '& span.MuiAutocomplete-tag': {
    margin: '0 4px',
    padding: '5px 8px',
    borderRadius: '6px',
    backgroundColor: 'var(--primary-100)',
    color: 'var(--primary)',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '14px'
  }
};

const DevicesChipsAutocomplete: React.FC<DevicesChipsAutocompleteProps> = ({
  value,
  options,
  onChange,
  placeholder,
  limitTags = 3,
  disabled,
  chipIcon
}) => {
  const { t } = useTranslation();

  return (
    <Autocomplete
      multiple
      fullWidth
      disableCloseOnSelect
      disabled={disabled}
      value={value}
      options={options}
      onChange={(_, nextValue) => onChange(nextValue)}
      isOptionEqualToValue={(option, selected) =>
        getDeviceKey(option) === getDeviceKey(selected)
      }
      getOptionLabel={(device) => device.name}
      popupIcon={<ArrowDownIcon sx={{ width: '18px !important' }} />}
      noOptionsText={t('no_results_found')}
      renderValue={(devices, getItemProps) => {
        const chips = devices.slice(0, limitTags).map((device, index) => {
          const { key, ...itemProps } = getItemProps({ index });

          return (
            <Chip
              {...itemProps}
              key={key}
              icon={chipIcon}
              label={device.name}
            />
          );
        });
        const hiddenCount = devices.length - chips.length;

        if (!hiddenCount) return chips;

        return [
          ...chips,
          <span key="hidden-count" className="MuiAutocomplete-tag">
            +{hiddenCount}
          </span>
        ];
      }}
      renderOption={({ key, ...optionProps }, option, { selected }) => (
        <li
          {...optionProps}
          key={key}
          data-selected={selected}
          className={`${optionProps.className} flex items-center gap-2`}
        >
          {chipIcon}
          <span className="truncate">{option.name}</span>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value.length ? undefined : placeholder}
        />
      )}
      slotProps={{
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, 6] } }]
        },
        clearIndicator: { title: t('clear_selection') }
      }}
      sx={CHIP_TAGS_SX}
    />
  );
};

export default DevicesChipsAutocomplete;
